import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin Login — TheKhoj";
    if (localStorage.getItem("khoj_admin_token")) navigate("/admin/dashboard");
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("khoj_admin_token", data.token);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-card border border-border rounded-3xl p-8 sm:p-10 shadow-xl"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-11 h-11 rounded-full overflow-hidden ring-1 ring-border">
            <img src="/logos/compass_mark.png" alt="TheKhoj compass logo" className="w-full h-full object-cover" />
          </span>
          <div>
            <p className="font-display font-extrabold text-lg leading-tight">TheKhoj Admin</p>
            <p className="text-xs text-muted-foreground">Business dashboard login</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-xs font-semibold mb-1.5">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="admin email" data-testid="admin-login-email-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5">Password</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="••••••••" data-testid="admin-login-password-input"
            />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
            data-testid="admin-login-submit-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <a href="/" className="block text-center text-xs text-muted-foreground mt-6 hover:text-foreground transition-colors" data-testid="admin-login-back-link">
          ← Back to website
        </a>
      </motion.div>
    </div>
  );
}
